/**
 * Extract a human-readable message from an unknown failure value.
 *
 * THE BUG THIS EXISTS FOR: a PostgREST / Supabase failure is a PLAIN OBJECT
 * (`{ message, details, hint, code }`), never an `Error` instance. So the very
 * common idiom
 *
 *     error instanceof Error ? error.message : String(error)
 *
 * discards it twice over: `instanceof Error` is false, and `String({…})` is the
 * literal `"[object Object]"`. Reading only `.message` is not enough either —
 * PostgREST leaves `message` empty on several failures and puts the actual
 * reason in `details` or `hint`. The observed result was log lines and tool
 * results reading `"[object Object]"`, `""` or `"Unknown error"` on exactly the
 * runs that were worth diagnosing.
 *
 * Five independent workstreams hit this shape in different files without
 * knowing about each other (Meridian webinar executors, chat tool-error
 * logging, the QuickBooks recurring sync, the Sheets writer, the calendar
 * reader), which is what promoted the fix from a local helper to this package.
 *
 * WHY THIS PACKAGE: `@wtc/api-errors` is already the canonical, cross-app home
 * for "a Postgres/Supabase error reached us, now what" — it owns
 * `mapToUserFacingError` and the `PostgresLikeError` shape this reads. The two
 * halves belong together: `mapToUserFacingError` decides what the CLIENT is
 * allowed to see, `extractErrorMessage` decides what the LOG, the alert and the
 * model see. Putting it here (rather than in `apps/hub/lib/utils`) also keeps it
 * reachable from Nonprofit Hub and every other app without a forbidden cross-app
 * import, and the package is dependency-free so nothing about importing it is
 * expensive.
 *
 * @see ./user-facing-errors.ts
 */

import type { PostgresLikeError } from "./user-facing-errors";

/** Options for {@link extractErrorMessage}. */
export interface ExtractErrorMessageOptions {
  /**
   * Returned when nothing readable could be found. Defaults to
   * `"Unknown error"`. Never returns `"[object Object]"` whatever you pass.
   */
  fallback?: string;
}

/**
 * True when `value` looks like a PostgREST/Supabase error object: a non-null
 * object carrying at least one of the fields PostgREST populates. Deliberately
 * structural — the Supabase client does not export a class to `instanceof`
 * against, and errors also arrive re-serialized across a queue or an HTTP hop,
 * which would defeat one anyway.
 */
export function isPostgrestLikeError(value: unknown): value is PostgresLikeError {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.message === "string" ||
    typeof candidate.details === "string" ||
    typeof candidate.hint === "string" ||
    typeof candidate.code === "string"
  );
}

/**
 * Turn any caught/returned failure value into the most informative string
 * available.
 *
 * Order, and why each rung exists:
 *  1. `Error` WITH a non-empty message — the ordinary case.
 *  2. a plain string — some libraries reject with one.
 *  3. `message` → `details` → `hint`, first non-empty wins. This is the rung
 *     the naive idiom misses entirely, and the reason an `Error` with an EMPTY
 *     message falls through to here instead of returning `""`: a
 *     `StorageError`/`AuthError` can be a real `Error` and still carry the
 *     reason in `details`.
 *  4. `code` alone — not prose, but it is the handle someone can search for,
 *     and it beats `"[object Object]"`.
 *  5. the JSON shape, which at least names its keys (guarded: a circular value
 *     would throw).
 *  6. `fallback`.
 *
 * A primitive (number, boolean, symbol) stringifies normally; `null`/`undefined`
 * return the fallback rather than the words "null"/"undefined", which read as
 * data rather than as "we do not know".
 */
export function extractErrorMessage(
  error: unknown,
  options: ExtractErrorMessageOptions = {},
): string {
  const fallback = options.fallback ?? "Unknown error";

  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string") return error.length > 0 ? error : fallback;

  if (error !== null && typeof error === "object") {
    const candidate = error as {
      message?: unknown;
      details?: unknown;
      hint?: unknown;
      code?: unknown;
    };
    for (const field of [candidate.message, candidate.details, candidate.hint]) {
      if (typeof field === "string" && field.length > 0) return field;
    }
    if (typeof candidate.code === "string" && candidate.code.length > 0) {
      return `error code ${candidate.code}`;
    }
    if (typeof candidate.code === "number") {
      return `error code ${candidate.code}`;
    }
    try {
      const serialized = JSON.stringify(error);
      if (serialized !== undefined && serialized !== "{}" && serialized !== "[]") {
        return serialized;
      }
    } catch {
      // circular / non-serializable — fall through
    }
    return fallback;
  }

  if (error === null || error === undefined) return fallback;

  return String(error);
}

/**
 * Extract the structured error code, when there is one. Kept next to
 * {@link extractErrorMessage} because callers that log one almost always want
 * the other, and a SQLSTATE is the field that makes a log line greppable.
 */
export function extractErrorCode(error: unknown): string | undefined {
  if (error !== null && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && code.length > 0) return code;
    if (typeof code === "number") return String(code);
  }
  return undefined;
}

/**
 * `message` plus the PostgREST fields the message alone leaves out, for a log
 * line or an alert where the extra context is worth the width. Never duplicates
 * a field it already used as the message.
 *
 * `"could not embed … (hint: perhaps you meant …) [PGRST201]"`
 */
export function describeError(error: unknown, options: ExtractErrorMessageOptions = {}): string {
  const message = extractErrorMessage(error, options);
  if (error === null || typeof error !== "object") return message;

  const candidate = error as { details?: unknown; hint?: unknown };
  const parts: string[] = [message];

  if (typeof candidate.details === "string" && candidate.details.length > 0 && candidate.details !== message) {
    parts.push(`details: ${candidate.details}`);
  }
  if (typeof candidate.hint === "string" && candidate.hint.length > 0 && candidate.hint !== message) {
    parts.push(`hint: ${candidate.hint}`);
  }

  const code = extractErrorCode(error);
  const suffix = code && !message.includes(code) ? ` [${code}]` : "";

  return parts.length === 1 ? `${parts[0]}${suffix}` : `${parts[0]} (${parts.slice(1).join("; ")})${suffix}`;
}
